// Sprite masks are avoided: rendering them into a RenderTexture crashed the
// decal system before, so Canvas2D keeps the baked layers to the art's alpha.
import * as PIXI from 'pixi.js';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { simulationFrame } from '../SimulationClock';
import { boneAxis } from './goreGeometry';
import { artOf, bloodOf, css, destroyMesh, drawExtras, extrasOf, meshOf, paint, shapeBounds, tracePath } from './goreArt';
import { PALETTE, tearPieces } from './goreTear';

const SLOT = 128;
const ATLAS_SIZE = 1024;
const BUDGET_MS = 3;
// Chunks that wait longer than this for a bake show as meat instead, so none
// pops in late and a big burst never keeps baking for long.
const WAIT_FRAMES = 4;
const PREWARM_MS = 1.5;
const FALLBACK = 'Gore_Meat10000';

const PARTS = {
	head: {lines: 2, inside: 'skull'},
	body: {lines: 2, inside: 'ribs'},
	belly: {lines: 1, inside: 'guts'},
	thigh_left: {lines: 1, bone: true}, thigh_right: {lines: 1, bone: true},
	leg_left: {lines: 1, bone: true}, leg_right: {lines: 1, bone: true},
	shoulder_left: {lines: 1, bone: true}, shoulder_right: {lines: 1, bone: true},
	arm_left: {lines: 1, bone: true}, arm_right: {lines: 1, bone: true},
};

let atlas = null;
let budgetFrame = -1;
let budgetUsed = 0;
let prewarmFrame = -1;
const prewarmQueue = [];
const bakeQueue = [];
const prepared = new WeakMap();

const layer = () => {
	const canvas = document.createElement('canvas');
	canvas.width = canvas.height = SLOT;
	const ctx = canvas.getContext('2d');
	const texture = PIXI.Texture.from(canvas);
	return {canvas, ctx, texture, sprite: new PIXI.Sprite(texture)};
};

const getAtlas = () => {
	if (atlas && !atlas.texture.destroyed) return atlas;
	const texture = PIXI.RenderTexture.create({width: ATLAS_SIZE, height: ATLAS_SIZE});
	const slots = [];
	for (let y = 0; y < ATLAS_SIZE; y += SLOT) {
		for (let x = 0; x < ATLAS_SIZE; x += SLOT) {
			slots.push({x, y, texture: new PIXI.Texture({source: texture.source, frame: new PIXI.Rectangle(x, y, SLOT, SLOT)}), sprite: null, pending: false, time: 0});
		}
	}
	const eraser = new PIXI.Graphics().rect(0, 0, SLOT, SLOT).fill(0xffffff);
	eraser.blendMode = 'erase';
	atlas = {texture, slots, bySlotTexture: new Map(slots.map(slot => [slot.texture, slot])), root: new PIXI.Container(), eraser, under: layer(), over: layer()};
	return atlas;
};

const isFree = slot => !slot.pending && !slot.queued && (!slot.sprite || slot.sprite.destroyed || !slot.sprite.visible || slot.sprite.texture !== slot.texture);

const toMeat = sprite => {
	sprite.texture = PIXI.Texture.from(FALLBACK);
	sprite.pivot.set(sprite.width / 2, sprite.height / 2);
};

// A free slot, or the oldest one. A chunk still flying on that one turns into meat.
const takeSlot = now => {
	const {slots} = getAtlas();
	let slot = slots.find(isFree);
	if (!slot) {
		slot = slots.reduce((oldest, candidate) => !candidate.pending && !candidate.queued && (!oldest || candidate.time < oldest.time) ? candidate : oldest, null);
		if (!slot) return null;
		const sprite = slot.sprite;
		if (sprite && !sprite.destroyed && sprite.texture === slot.texture) toMeat(sprite);
	}
	slot.sprite = null;
	slot.pending = true;
	slot.time = now;
	return slot;
};

// A cut part draws its eyes and such as meshes; its cut state keeps where they go.
const extrasFor = skin => skin.goreCut
	? skin.goreCut.extras.map(extra => ({texture: extra.texture, x: extra.x, y: extra.y, rotation: 0, scale: {x: 1, y: 1}, anchor: {x: 0, y: 0}}))
	: extrasOf(skin);

const bake = (slot, piece, art, skin) => {
	const {root, eraser, under, over} = atlas;
	const box = shapeBounds(piece.overlay, shapeBounds(piece.outer.map(v => ({x: v.x, y: v.y, r: 0.5}))));
	const scaleX = skin.scale.x;
	const scaleY = skin.scale.y;
	const fit = Math.min(1, (SLOT - 4) / Math.max((box.maxX - box.minX) * Math.abs(scaleX), (box.maxY - box.minY) * Math.abs(scaleY)));
	const kx = scaleX * fit;
	const ky = scaleY * fit;
	const ox = SLOT / 2 - kx * (box.minX + box.maxX) / 2;
	const oy = SLOT / 2 - ky * (box.minY + box.maxY) / 2;

	const u = under.ctx;
	u.setTransform(1, 0, 0, 1, 0, 0);
	u.clearRect(0, 0, SLOT, SLOT);
	u.setTransform(kx, 0, 0, ky, ox, oy);
	u.lineCap = u.lineJoin = 'round';
	u.save();
	u.beginPath();
	tracePath(u, piece.outer);
	u.clip();
	u.beginPath();
	tracePath(u, piece.outer);
	if (piece.inner) tracePath(u, piece.inner);
	u.fillStyle = css(PALETTE.meat);
	u.fill('evenodd');
	paint(u, piece.under);
	u.restore();
	u.globalCompositeOperation = 'destination-in';
	u.drawImage(art.canvas, 0, 0);
	u.globalCompositeOperation = 'source-over';

	const o = over.ctx;
	o.setTransform(1, 0, 0, 1, 0, 0);
	o.clearRect(0, 0, SLOT, SLOT);
	o.setTransform(kx, 0, 0, ky, ox, oy);
	o.lineCap = o.lineJoin = 'round';
	if (piece.inner) {
		o.save();
		o.beginPath();
		tracePath(o, piece.inner);
		o.clip();
		drawExtras(o, extrasFor(skin));
		o.restore();
	}
	o.save();
	o.beginPath();
	tracePath(o, piece.outer);
	o.clip();
	paint(o, piece.over);
	o.restore();
	o.globalCompositeOperation = 'destination-in';
	o.drawImage(art.canvas, 0, 0);
	o.globalCompositeOperation = 'source-over';
	paint(o, piece.overlay);
	under.texture.source.update();
	over.texture.source.update();

	const meshes = [
		skin.myFlesh && meshOf(piece.outer, skin.myFlesh.texture, skin.myFlesh.tint),
		meshOf(piece.inner, skin.originalSprite.texture, skin.originalSprite.tint),
	];
	for (const mesh of meshes) {
		if (!mesh) continue;
		mesh.position.set(slot.x + ox, slot.y + oy);
		mesh.scale.set(kx, ky);
	}
	eraser.position.set(slot.x, slot.y);
	under.sprite.position.set(slot.x, slot.y);
	over.sprite.position.set(slot.x, slot.y);
	root.addChild(eraser, ...[meshes[0], under.sprite, meshes[1], over.sprite].filter(Boolean));
	game.app.renderer.render({container: root, target: atlas.texture, clear: false});
	root.removeChildren();
	meshes.forEach(destroyMesh);
};

const artOfSkin = (skin, bone) => {
	const art = artOf(PIXI.Texture.from(skin.data.textureName));
	if (art && bone && art.bone === undefined) art.bone = boneAxis(art.rgba, art.w, art.h);
	return art;
};

// A part a blade cut before tears from what is left of it, along fewer lines.
const piecesFor = (body, skin, spec, art) => tearPieces(art, {
	lines: Math.max(1, spec.lines - (skin.goreCut?.lines.length || 0)), inside: spec.inside, bone: spec.bone ? art.bone : null,
	blood: bloodOf(body), seed: Math.floor(Math.random() * 0x7fffffff), start: skin.goreCut?.cell, cuts: skin.goreCut?.lines,
});

const piecesOf = (body, skin, spec, art) => {
	const ready = prepared.get(skin);
	prepared.delete(skin);
	return ready?.art === art && !skin.goreCut ? ready.pieces : piecesFor(body, skin, spec, art);
};

const spendBudget = () => {
	const frame = simulationFrame();
	if (frame !== budgetFrame) {
		budgetFrame = frame;
		budgetUsed = 0;
	}
	return budgetUsed < BUDGET_MS;
};

// Visual only, so Math.random is fine. Returns fewer textures than asked for
// when the part has no art to tear.
export const tearChunks = (body, part, count) => {
	const spec = PARTS[part];
	const skin = body.myTexture;
	if (!spec || !skin || skin.destroyed || !skin.originalSprite || !game.app?.renderer) return [];
	const started = performance.now();
	// Over the frame's budget the chunks fly hidden until a later frame bakes them.
	if (!spendBudget()) {
		const slots = [];
		for (let i = 0; i < count; i++) {
			const slot = takeSlot(started);
			if (!slot) break;
			slot.queued = true;
			slots.push(slot);
		}
		bakeQueue.push({body, skin, spec, slots, frame: simulationFrame()});
		return slots.map(slot => slot.texture);
	}
	const art = artOfSkin(skin, spec.bone);
	if (!art) return [];
	const textures = [];
	for (const piece of piecesOf(body, skin, spec, art).slice(0, count)) {
		const slot = takeSlot(started);
		if (!slot) break;
		bake(slot, piece, art, skin);
		textures.push(slot.texture);
	}
	budgetUsed += performance.now() - started;
	return textures;
};

const bakeQueued = ({body, skin, spec, slots}, inTime) => {
	const art = inTime && !skin.destroyed && artOfSkin(skin, spec.bone);
	const pieces = art ? piecesOf(body, skin, spec, art) : [];
	slots.forEach((slot, i) => {
		slot.queued = false;
		const sprite = slot.sprite;
		const flying = sprite && !sprite.destroyed && sprite.texture === slot.texture;
		if (pieces[i]) bake(slot, pieces[i], art, skin);
		else if (flying) toMeat(sprite);
		if (flying) sprite.renderable = true;
	});
};

// Sprite i shows texture i.
export const adoptChunks = (textures, sprites) => {
	if (!atlas) return;
	sprites.forEach((sprite, i) => {
		const slot = atlas.bySlotTexture.get(textures[i]);
		if (!slot || sprite.texture !== slot.texture) return;
		slot.sprite = sprite;
		if (slot.queued) sprite.renderable = false;
	});
	for (const slot of atlas.slots) slot.pending = false;
};

// A chunk shown by another sprite now keeps its slot through that sprite.
export const handOverChunk = (from, to) => {
	const slot = atlas?.slots.find(slot => slot.sprite === from && from.texture === slot.texture);
	if (slot) slot.sprite = to;
};

// The first read of the character atlas back from the GPU and the first bake
// are slow, so they happen while the game loads.
export const warmGore = () => {
	if (!game.app?.renderer || atlas) return;
	const name = 'Normal_Head_Idle0000';
	const art = artOf(PIXI.Texture.from(name));
	getAtlas();
	if (!art) return;
	const skin = new PIXI.Container();
	const sprite = new PIXI.Sprite(PIXI.Texture.from(name));
	const flesh = new PIXI.Sprite(PIXI.Texture.from('Normal_Head_Flesh0000'));
	skin.addChild(flesh, sprite);
	Object.assign(skin, {originalSprite: sprite, myFlesh: flesh, data: {textureName: name}});
	const slot = atlas.slots[atlas.slots.length - 1];
	bake(slot, tearPieces(art, {lines: 2, inside: 'skull', seed: 1})[0], art, skin);
	slot.pending = false;
	skin.destroy({children: true});
};

export const prewarmGore = (bodies, tearable) => {
	if (!Settings.goreEnabled || !game.app?.renderer) return;
	for (const body of bodies) {
		const skin = body.myTexture;
		const part = body.mySprite?.data?.refName;
		if (!skin || !skin.data?.textureName) continue;
		const art = artOfSkin(skin, /^(thigh|leg|shoulder|arm)_/.test(part));
		if (art && tearable && PARTS[part]) prewarmQueue.push({body, skin, part});
	}
};

export const pumpGorePrewarm = () => {
	if (!prewarmQueue.length && !bakeQueue.length) return;
	const frame = simulationFrame();
	if (frame === prewarmFrame) return;
	prewarmFrame = frame;
	while (bakeQueue.length) {
		const inTime = frame - bakeQueue[0].frame <= WAIT_FRAMES;
		if (inTime && !spendBudget()) break;
		const started = performance.now();
		bakeQueued(bakeQueue.shift(), inTime);
		budgetUsed += performance.now() - started;
	}
	const started = performance.now();
	while (prewarmQueue.length && performance.now() - started < PREWARM_MS) {
		const {body, skin, part} = prewarmQueue.shift();
		if (skin.destroyed || body.myTexture !== skin || prepared.has(skin)) continue;
		const art = artOfSkin(skin, PARTS[part].bone);
		if (art) prepared.set(skin, {art, pieces: piecesFor(body, skin, PARTS[part], art)});
	}
};
